<template>
  <div class="book-detailed-view min-h-screen bg-background/50 dark:bg-background/80">
    <BubbleHeader :bubble="bubble" />
    <div class="max-w-7xl mx-auto p-4 sm:p-6 space-y-8">
      <!-- Hero Section -->
      <div
        class="relative overflow-hidden bg-gradient-to-br from-card to-card/50 rounded-xl shadow-lg dark:from-card/80 dark:to-card/30"
      >
        <div
          class="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/0 dark:from-primary/10 dark:to-primary/0"
        ></div>
        <div class="relative flex flex-col md:flex-row p-6 sm:p-8 gap-8">
          <div class="relative md:w-1/3 flex-shrink-0">
            <div
              class="aspect-[2/3] rounded-lg overflow-hidden shadow-xl ring-1 ring-black/5 dark:ring-white/10"
            >
              <NuxtImg
                v-if="coverPhotoSrc"
                :src="coverPhotoSrc"
                :alt="bubble.title"
                class="w-full h-full object-cover transform transition-transform duration-300 hover:scale-105"
              />
              <div
                v-else
                class="w-full h-full bg-muted/50 dark:bg-muted/20 flex items-center justify-center"
              >
                <Icon name="ph:book-open" class="w-16 h-16 text-muted-foreground/50" />
              </div>
            </div>
          </div>

          <div class="md:w-2/3 flex flex-col justify-between">
            <div class="space-y-6">
              <div>
                <h1 class="text-4xl font-bold tracking-tight mb-2">{{ bubble.title }}</h1>
                <p class="text-xl text-muted-foreground">
                  by
                  <span class="font-medium">{{
                    bubble.authors?.join(', ') || 'Unknown Author'
                  }}</span>
                </p>
              </div>

              <div class="flex flex-wrap gap-3">
                <span
                  v-if="bubble.publicationDate"
                  class="px-3 py-1 bg-primary/5 hover:bg-primary/10 dark:bg-primary/10 dark:hover:bg-primary/15 text-primary/80 dark:text-primary-foreground/80 rounded-full text-sm font-medium transition-colors ring-1 ring-primary/10 dark:ring-primary/20"
                >
                  {{ formatDate(bubble.publicationDate) }}
                </span>
                <span
                  class="px-3 py-1 bg-primary/5 hover:bg-primary/10 dark:bg-primary/10 dark:hover:bg-primary/15 text-primary/80 dark:text-primary-foreground/80 rounded-full text-sm font-medium transition-colors ring-1 ring-primary/10 dark:ring-primary/20"
                >
                  {{ bubble.pageCount }} pages
                </span>
                <span
                  v-if="bubble.publisher"
                  class="px-3 py-1 bg-primary/5 hover:bg-primary/10 dark:bg-primary/10 dark:hover:bg-primary/15 text-primary/80 dark:text-primary-foreground/80 rounded-full text-sm font-medium transition-colors ring-1 ring-primary/10 dark:ring-primary/20"
                >
                  {{ bubble.publisher }}
                </span>
              </div>

              <p
                v-if="bubble.description"
                class="text-lg text-muted-foreground leading-relaxed prose dark:prose-invert line-clamp-4"
                v-html="sanitizeHtml(bubble.description)"
              ></p>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div v-if="bubble.isbn13" class="flex items-center gap-3">
                  <div class="p-2 rounded-full bg-muted/50 dark:bg-muted/20">
                    <Icon name="ph:barcode" class="w-5 h-5 text-primary" />
                  </div>
                  <span class="text-sm"
                    >ISBN-13: <span class="font-medium">{{ bubble.isbn13 }}</span></span
                  >
                </div>
                <div v-if="bubble.isbn10" class="flex items-center gap-3">
                  <div class="p-2 rounded-full bg-muted/50 dark:bg-muted/20">
                    <Icon name="ph:barcode" class="w-5 h-5 text-primary" />
                  </div>
                  <span class="text-sm"
                    >ISBN-10: <span class="font-medium">{{ bubble.isbn10 }}</span></span
                  >
                </div>
              </div>
            </div>

            <div class="flex flex-wrap gap-4 mt-8">
              <a
                v-if="bubble.resourceLink"
                :href="bubble.resourceLink"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-2 px-4 py-2 bg-card/50 dark:bg-card/30 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group"
              >
                <Icon
                  name="ph:link"
                  class="w-5 h-5 text-primary transition-transform duration-200 group-hover:scale-110"
                />
                <span class="font-medium">View Resource</span>
              </a>
              <a
                v-if="bubble.googleBookLink"
                :href="bubble.googleBookLink"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-2 px-4 py-2 bg-card/50 dark:bg-card/30 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 group"
              >
                <Icon
                  name="simple-icons:google"
                  class="w-5 h-5 text-primary transition-transform duration-200 group-hover:scale-110"
                />
                <span class="font-medium">View on Google Books</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Categories and Stats Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Categories Section -->
        <div
          v-if="bubble.categories && bubble.categories.length > 0"
          class="bg-card dark:bg-card/50 rounded-xl shadow-lg p-6 transition-all duration-200 hover:shadow-xl"
        >
          <h2 class="text-xl font-semibold mb-6 flex items-center gap-2">
            <Icon name="ph:tag" class="w-5 h-5 text-primary" />
            Categories
          </h2>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="category in bubble.categories"
              :key="category"
              class="px-3 py-1.5 bg-muted/30 hover:bg-muted/50 dark:bg-muted/10 dark:hover:bg-muted/20 text-foreground/80 dark:text-foreground/70 rounded-full text-sm font-medium transition-colors duration-200"
            >
              {{ category }}
            </span>
          </div>
        </div>

        <!-- Stats Section -->
        <div
          class="bg-card dark:bg-card/50 rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl"
        >
          <div class="p-6 border-b border-border dark:border-border/50">
            <h2 class="text-xl font-semibold flex items-center gap-2">
              <Icon name="ph:chart-bar" class="w-5 h-5 text-primary" />
              Your Stats
            </h2>
          </div>
          <BubbleStats
            :bubble-uuid="bubble.uuid"
            did-label="Read"
            placeholder="Write your thoughts about this book..."
          />
        </div>
      </div>

      <!-- Full Description -->
      <div
        v-if="bubble.description"
        class="bg-card dark:bg-card/50 rounded-xl shadow-lg p-6 transition-all duration-200 hover:shadow-xl"
      >
        <h2 class="text-xl font-semibold mb-6 flex items-center gap-2">
          <Icon name="ph:book-open-text" class="w-5 h-5 text-primary" />
          Description
        </h2>
        <div
          class="text-muted-foreground leading-relaxed prose dark:prose-invert max-w-none"
          v-html="sanitizeHtml(bubble.description)"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BookBubble } from '@/stores/interfaces'
import BubbleHeader from '../BubbleHeader.vue'
import { computed } from 'vue'
import { useRuntimeConfig } from '#app'
import BubbleStats from '../BubbleStats.vue'
import DOMPurify from 'isomorphic-dompurify'
import { useTypeNavigation } from '@/composables/useTypeNavigation'

const {
  public: { dataStore },
} = useRuntimeConfig()

const props = defineProps<{
  bubble: BookBubble
}>()

// Use the type navigation composable
useTypeNavigation(props.bubble)

const formatDate = (dateString: string | null) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const coverPhotoSrc = computed(() => {
  // Try live cover photo first
  if (props.bubble.mediumThumbnail) {
    return props.bubble.mediumThumbnail
  }
  // Fallback to offline version if available
  if (props.bubble.offline && props.bubble.offlineMediaResources?.cover_photo) {
    return `${dataStore}${props.bubble.offlineMediaResources.cover_photo}`
  }
  return null
})

const sanitizeHtml = (html: string) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  })
}
</script>

<style scoped>
.book-detailed-view {
  background-image:
    radial-gradient(circle at top left, var(--primary-50) 0%, transparent 20%),
    radial-gradient(circle at bottom right, var(--primary-50) 0%, transparent 20%);
}

@media (prefers-color-scheme: dark) {
  .book-detailed-view {
    background-image:
      radial-gradient(circle at top left, var(--primary-900) 0%, transparent 25%),
      radial-gradient(circle at bottom right, var(--primary-900) 0%, transparent 25%);
  }
}
</style>
